import { Link } from 'react-router-dom'
import Logo from './Logo'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const trustpilotUrl = 'https://www.trustpilot.com/review/gaiapowered.co.uk'

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-5 h-5 ${
              star <= rating ? 'text-[#00B67A]' : 'text-gray-500'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    )
  }

  return (
    <footer className="bg-dark-background text-white">
      {/* Trustpilot Banner */}
      <div className="border-t border-gray-700 bg-gradient-to-r from-primary-green to-secondary-green">
        <div className="container-custom py-6">
          <a
            href={trustpilotUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col md:flex-row items-center justify-center gap-4 hover:opacity-90 transition-opacity"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg font-semibold text-white">Rated Excellent on</span>
              <span className="text-2xl font-bold text-white">Trustpilot</span>
            </div>
            <div className="flex items-center gap-3">
              {renderStars(4)}
              <span className="text-lg font-semibold text-white">4.4 / 5</span>
              <span className="text-green-100">Based on 12 reviews</span>
            </div>
          </a>
        </div>
      </div>

      <div className="container-custom pt-20 pb-30">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 pt-6 pb-20">
          <div className="md:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <Logo variant="light" />
            </Link>
            <p className="text-gray-100 mb-4 text-md max-w-md">
              Your trusted partner for eco-friendly, cost-saving energy solutions. 
              Specializing in solar panels, heat pumps, and EV charging installations 
              for UK homeowners.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/gaiapowered"
                target="_blank"
                className="text-gray-400 hover:text-primary-green transition-colors"
                aria-label="Follow us on Facebook"
              >
                <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/gaia-powered/"
                className="text-gray-400 hover:text-primary-green transition-colors"
                target="_blank"
                aria-label="Follow us on LinkedIn"
              >
                <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/gaiapoweredltd/"
                className="text-gray-400 hover:text-primary-green transition-colors"
                target="_blank"
                aria-label="Follow us on Instagram"
              >
                <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-poppins font-semibold mb-4 text-white">Services</h4>
            <nav className="space-y-2">
              <Link 
                to="/solar-panels" 
                className="block text-gray-300 hover:text-primary-green transition-colors"
              >
                Solar Panels
              </Link>
              <Link 
                to="/heat-pumps" 
                className="block text-gray-300 hover:text-primary-green transition-colors"
              >
                Heat Pumps
              </Link>
            </nav>
          </div>

          <div>
            <h4 className="font-poppins font-semibold mb-4 text-white">Company</h4>
            <nav className="space-y-2">
              <Link
                to="/about"
                className="block text-gray-300 hover:text-primary-green transition-colors"
              >
                About Us
              </Link>
              {/* <Link
                to="/case-studies"
                className="block text-gray-300 hover:text-primary-green transition-colors"
              >
                Case Studies
              </Link> */}
              <Link
                to="/work-with-us"
                className="block text-gray-300 hover:text-primary-green transition-colors"
              >
                Work with Us
              </Link>
              <Link
                to="/contact"
                className="block text-gray-300 hover:text-primary-green transition-colors"
              >
                Contact
              </Link>
            </nav>
          </div>

          <div>
            <h4 className="font-poppins font-semibold mb-4 text-white">Contact Info</h4>
            <div className="space-y-2 text-gray-300">
              <p>
                <a
                  href={`tel:${import.meta.env.VITE_COMPANY_PHONE?.replace(/\s/g, '')}`}
                  className="hover:text-primary-green transition-colors"
                >
                  {import.meta.env.VITE_COMPANY_PHONE}
                </a>
              </p>
              <p>
                <a
                  href={`mailto:${import.meta.env.VITE_COMPANY_SALES_EMAIL}`}
                  className="hover:text-primary-green transition-colors"
                >
                  {import.meta.env.VITE_COMPANY_SALES_EMAIL}
                </a>
              </p>
              <p className="text-sm">
                {import.meta.env.VITE_COMPANY_ADDRESS}
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-poppins font-semibold mb-4 text-white">Legal</h4>
            <nav className="space-y-2">
              <Link
                to="/privacy-policy"
                className="block text-gray-300 hover:text-primary-green transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/cookies"
                className="block text-gray-300 hover:text-primary-green transition-colors"
              >
                Cookie Policy
              </Link>
              <Link
                to="/customer-care"
                className="block text-gray-300 hover:text-primary-green transition-colors"
              >
                Customer Care
              </Link>
              <Link
                to="/contact"
                className="block text-gray-300 hover:text-primary-green transition-colors"
              >
                Contact Us
              </Link>
            </nav>
          </div>
        </div>

        </div>
        <div className="border-t border-gray-700 mt-10 pt-8 pb-8 text-center text-gray-400">
          <p>&copy; {currentYear} Gaia Powered Limited, 16126166. All rights reserved.</p>
          <a
          href="https://naut.digital"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white/30 hover:text-nelson-light-teal transition-colors text-[10px] tracking-wider"
        >
          Website by naut.digital
        </a>
      </div>
    </footer>
  )
}

export default Footer